/**
 * Thin auto-reconnecting WebSocket wrapper.
 *
 * Scope:
 *  - JWT is passed as ?token=... since browsers can't set headers on WS.
 *  - Exponential backoff with jitter (1s → 30s) on unexpected close.
 *  - Heartbeat ping every 25s so load balancers don't idle-cull the socket.
 *  - Message handlers are registered per kind (e.g. "notification",
 *    "presence") so callers can subscribe to just what they care about.
 *
 * Not in scope:
 *  - Guaranteed delivery. The REST endpoints remain the durable source of
 *    truth; WS messages are purely a cache-invalidation hint for the UI.
 */
import { API_BASE_URL } from "./constants";
import { getAccessToken } from "@/api/client";

export interface WsMessage {
  kind: string;
  // All other fields are kind-specific; we narrow in the consumer.
  [key: string]: unknown;
}

export type WsHandler = (msg: WsMessage) => void;

/** Derive the WS base URL from the REST base URL. */
function wsOrigin(): string {
  // API_BASE_URL is either absolute (https://api.x/api/v1) or relative
  // (/api/v1). In either case the WS path is served from the same origin.
  if (API_BASE_URL.startsWith("http://") || API_BASE_URL.startsWith("https://")) {
    const u = new URL(API_BASE_URL);
    const proto = u.protocol === "https:" ? "wss:" : "ws:";
    return `${proto}//${u.host}`;
  }
  if (typeof window !== "undefined") {
    const proto = window.location.protocol === "https:" ? "wss:" : "ws:";
    return `${proto}//${window.location.host}`;
  }
  return "";
}

interface OpenArgs {
  path: string;
  params?: Record<string, string>;
  onMessage: WsHandler;
  onOpen?: () => void;
  onClose?: () => void;
}

export interface WsHandle {
  close: () => void;
  send: (data: string) => void;
}

export function openWs(args: OpenArgs): WsHandle {
  let ws: WebSocket | null = null;
  let closed = false;
  let attempts = 0;
  let heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null;

  // Pause behavior while the tab is hidden. Background tabs get setTimeout
  // throttled by Chrome but the reconnect loop still allocates WebSocket
  // objects (and their kernel FDs); over hours of idle that's enough to
  // OOM the renderer ("Aw, Snap! Error code: 5"). We stop scheduling
  // reconnects when hidden and resume on the next visibilitychange.
  const isHidden = (): boolean =>
    typeof document !== "undefined" && document.visibilityState === "hidden";

  const clearHeartbeat = () => {
    if (heartbeatTimer) {
      clearInterval(heartbeatTimer);
      heartbeatTimer = null;
    }
  };

  const clearReconnect = () => {
    if (reconnectTimer) {
      clearTimeout(reconnectTimer);
      reconnectTimer = null;
    }
  };

  const scheduleReconnect = (waitMs: number) => {
    clearReconnect();
    if (closed || isHidden()) return;
    reconnectTimer = setTimeout(() => {
      reconnectTimer = null;
      connect();
    }, waitMs);
  };

  const connect = () => {
    if (closed || isHidden()) return;
    const token = getAccessToken();
    if (!token) {
      // No session — back off and retry once the user logs in. Cheaper than
      // looping; the React layer will re-open after login anyway.
      attempts = Math.min(attempts + 1, 10);
      scheduleReconnect(Math.min(30_000, 1000 * 2 ** attempts));
      return;
    }
    const qp = new URLSearchParams({ token, ...(args.params || {}) });
    const url = `${wsOrigin()}${args.path}?${qp.toString()}`;

    ws = new WebSocket(url);

    ws.onopen = () => {
      attempts = 0;
      args.onOpen?.();
      // Heartbeat — mirrors the backend's "ping"/"pong" convention.
      clearHeartbeat();
      heartbeatTimer = setInterval(() => {
        if (isHidden()) return; // don't ping while hidden
        try {
          ws?.send("ping");
        } catch {
          // ignore — next onclose will trigger reconnect
        }
      }, 25_000);
    };

    ws.onmessage = (ev) => {
      try {
        const msg = JSON.parse(ev.data) as WsMessage;
        args.onMessage(msg);
      } catch {
        // Probably a "pong" text frame — ignore.
      }
    };

    ws.onclose = (ev) => {
      clearHeartbeat();
      args.onClose?.();
      if (closed) return;
      // 4401 = auth failure; don't hammer it, wait for next login.
      if (ev.code === 4401) return;
      // Cap attempts so the exponent doesn't run away. The wait is already
      // clamped at 30s, but bounding the counter keeps the math sane.
      attempts = Math.min(attempts + 1, 10);
      const wait =
        Math.min(30_000, 500 * 2 ** attempts) + Math.random() * 500;
      scheduleReconnect(wait);
    };

    ws.onerror = () => {
      // onclose will fire right after; nothing useful to do here.
    };
  };

  // Re-arm on visibility change so the user returning to the tab triggers
  // an immediate reconnect attempt (instead of waiting out the rest of the
  // backoff that started while we were hidden).
  const onVisibility = () => {
    if (closed) return;
    if (document.visibilityState === "visible") {
      // Only reconnect if we aren't already open or in flight.
      if (!ws || ws.readyState === WebSocket.CLOSED) {
        attempts = 0;
        scheduleReconnect(0);
      }
    } else {
      // Going hidden — drop scheduled reconnect attempts so we don't
      // accumulate them while the tab is in the background.
      clearReconnect();
    }
  };
  if (typeof document !== "undefined") {
    document.addEventListener("visibilitychange", onVisibility);
  }

  connect();

  return {
    close: () => {
      closed = true;
      clearHeartbeat();
      clearReconnect();
      if (typeof document !== "undefined") {
        document.removeEventListener("visibilitychange", onVisibility);
      }
      try {
        ws?.close();
      } catch {
        // ignore
      }
      ws = null;
    },
    send: (data: string) => {
      try {
        ws?.send(data);
      } catch {
        // ignore — next reconnect will re-establish
      }
    },
  };
}
